'use client';
import { Client, ClientList, ClientSummary } from '@/types/client';
import { User, UserSummary } from '@/types/user';
import { setCurrentUserViewTimezone } from '@/app/lib/utils';
import { createContext, useContext, useEffect, useState } from 'react';

const UserContext = createContext<{
  user: User | null;
  userSummary: UserSummary | null;
  client: Client | null;
  clientSummary: ClientSummary | null;
  clientList: ClientList | null;
  showOnboardingModal: boolean;
  setUser: (user: User | null) => void;
  setUserSummary: (userSummary: UserSummary | null) => void;
  setClient: (client: Client | null) => void;
  setClientSummary: (clientSummary: ClientSummary | null) => void;
  setClientList: (clientList: ClientList | null) => void;
  setShowOnboardingModal: (show: boolean) => void;
}>({
  user: null,
  userSummary: null,
  client: null,
  clientSummary: null,
  clientList: null,
  showOnboardingModal: false,
  setUser: () => {},
  setUserSummary: () => {},
  setClient: () => {},
  setClientSummary: () => {},
  setClientList: () => {},
  setShowOnboardingModal: () => {},
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userSummary, setUserSummary] = useState<UserSummary | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [clientSummary, setClientSummary] = useState<ClientSummary | null>(null);
  const [clientList, setClientList] = useState<ClientList | null>(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);

  useEffect(() => {
    setCurrentUserViewTimezone(user?.view_timezone);
  }, [user?.view_timezone]);

  return (
    <UserContext.Provider
      value={{
        user,
        userSummary,
        client,
        clientSummary,
        showOnboardingModal,
        setUser,
        setUserSummary,
        setClient,
        setClientSummary,
        clientList,
        setClientList,
        setShowOnboardingModal,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
